
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { StarBorder } from "@/components/ui/star-border"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-cta",
  {
    variants: {
      variant: {
        default: "bg-gold text-white hover:bg-gold/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90",
        outline:
          "border border-gold bg-transparent text-gold hover:bg-gold/10",
        secondary:
          "bg-luxury-800 text-white hover:bg-luxury-700",
        ghost: "hover:bg-gold/10 hover:text-gold text-white",
        link: "text-gold underline-offset-4 hover:underline",
        star: "p-0 bg-transparent", // New variant for StarBorder
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-6 sm:px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  starBorder?: boolean
  starColor?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, starBorder = false, starColor, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    if (starBorder) {
      return (
        <StarBorder 
          as={Comp} 
          className={cn(className, "text-sm sm:text-base font-cta")}
          color={starColor || "#c8a87d"}
          {...props}
        >
          {children}
        </StarBorder>
      )
    }
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), "text-sm sm:text-base")}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
